// Phase 24A — Last Login Report page.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, Loader2, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CommonFilterBar from "@/components/reports/CommonFilterBar";
import PresetsMenu from "@/components/reports/PresetsMenu";
import ExportButtons from "@/components/reports/ExportButtons";
import PageHeader from "@/components/PageHeader";

export default function LastLoginReport() {
  const [filters, setFilters] = useState({
    zone_id: "", sub_zone_id: "", role_code: "", user_id: "",
    date_from: "", date_to: "",
  });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page, page_size: 50 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data: d } = await api.get("/reports/last-login", { params });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const columns = data?.columns || [];
  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="last-login-report-page">
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: "/" }, { label: "Last login" }]}
        title="Last-login report"
        subtitle="When each user last authenticated, with device and location."
        favorite={{ key: "reports.last-login", type: "screen", label: "Last-login report", route: "/reports/last-login" }}
        testid="lastloginreport-page-header"
      />

      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Last Login Report</h1>
          <p className="text-xs text-neutral-500">Shows each user's most recent login.</p>
        </div>
        <div className="flex items-center gap-2">
          <PresetsMenu reportKey="last_login" filters={filters}
                          onApply={(f) => setFilters({ zone_id: "", sub_zone_id: "", role_code: "", user_id: "", date_from: "", date_to: "", ...f })} />
          <ExportButtons endpoint="/reports/last-login" params={filters} filename="last_login" />
          <Button variant="outline" size="sm" onClick={load} disabled={busy}
                    data-testid="refresh-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-1" />}
            Refresh
          </Button>
        </div>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-3">
          <CommonFilterBar value={filters} onChange={setFilters}
                              showZone showSubZone showRole showUser showDateRange />
        </CardContent>
      </Card>

      {data?.filter_summary && (
        <div className="text-xs text-neutral-500 flex items-center gap-1">
          <Info className="w-3 h-3" /> {data.filter_summary} · {total} rows
        </div>
      )}

      <Card className="border-neutral-200 rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="last-login-table">
              <thead className="bg-[#6D2C91] text-white sticky top-0">
                <tr>
                  {columns.map(c => (
                    <th key={c.key} className="text-left px-3 py-2 font-semibold whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={columns.length || 12}
                            className="text-center p-6 text-neutral-500">
                    No users match the filters.
                  </td></tr>
                ) : items.map((row, i) => (
                  <tr key={row.user_id + i} className="border-t hover:bg-neutral-50">
                    {columns.map(c => (
                      <td key={c.key} className="px-3 py-2 whitespace-nowrap">{row[c.key] ?? "—"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {total > 50 && (
        <div className="flex items-center justify-between text-xs text-neutral-500">
          <span>Page {page} of {Math.ceil(total / 50)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page * 50 >= total}
                      onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
